^<template>
    <b-modal id="schema-input-order-change-modal"
             hide-footer
             size="lg"
             modal-class="default-modal"
             @hide="closeModal"
    >
        <template #modal-title>
            <div class="d-flex align-items-center" style="gap: 8px;">
                {{ projectText }} - 스키마 입력 값 순서 변경
            </div>
        </template>

        <div class="action-btns">
            <b-button variant="primary"
                      :disabled="loading"
                      style="width: 100px"
                      @click="saveOrder"
            >
                순서 저장
            </b-button>
        </div>
        <div class="tabs-container">
            <b-tabs class="default-tabs with-actions" content-class="mt-3">
                <b-tab title="스키마 입력 값 순서 변경">
                    <div class="order-change-container">
                        <div class="order-list-header">
                            <h6>스키마 입력 값 목록</h6>
                            <small class="text-muted">드래그하여 순서를 변경하세요</small>
                        </div>
                        <draggable
                            v-model="localSchemaInputs"
                            handle=".drag-handle"
                            animation="200"
                            ghost-class="ghost-placeholder"
                            drag-class="dragging-item"
                            class="draggable-list"
                            :class="{'is-dragging': isDragging}"
                            @start="isDragging = true"
                            @end="isDragging = false"
                        >
                            <div
                                v-for="(schemaInput, index) in localSchemaInputs"
                                :key="schemaInput.id || index"
                                class="draggable-item"
                            >
                                <div class="drag-handle">
                                    <fa icon="grip-vertical" class="mr-2 handle"/>
                                </div>
                                <div class="item-content">
                                    <div class="item-order">{{ schemaInput.sort_order }}</div>
                                    <div class="item-info">
                                        <div class="item-basic">
                                            <div class="item-name">{{ schemaInput.key_name }}</div>
                                            <div class="item-code text-muted">
                                                {{ getValueTypeText(schemaInput) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </draggable>
                    </div>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import {updateInputKeys} from '@shared_utils/api/projectSchema';
import draggable from 'vuedraggable';
import {valueTypeOptions} from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/data';

export default {
    name: 'SchemaInputOrderChangeModal',
    components: {
        draggable
    },
    props: {
        projectText: {
            type: [String, null],
            default: null,
        },
        schemaInputs: {
            type: [Array, null],
            default: null
        },
    },
    data() {
        return {
            loading: false,
            isDragging: false,
            localSchemaInputs: [],
        };
    },
    computed: {
        getValueTypeText() {
            return (schemaInput) => {
                const target = valueTypeOptions.find((item) => item.value === schemaInput.value_type)?.text;
                if (target) {
                    return `${target} (${schemaInput.value_type})`;
                }
                return '-';
            };
          
        },
    },
    watch: {
        schemaInputs: {
            handler(newVal) {
                if (newVal && Array.isArray(newVal)) {
                    this.localSchemaInputs = [...newVal];
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.$root.$on('bv::modal::show', this.onShowSubChangeModal);
    },
    beforeDestroy() {
        this.$root.$off('bv::modal::show', this.onShowSubChangeModal);
    },
    methods: {
        onShowSubChangeModal(bvEvent, modalId){
            if (modalId !== 'schema-input-order-change-modal') return;
            this.localSchemaInputs = JSON.parse(JSON.stringify(this.schemaInputs));
        },
        closeModal() {
            this.localSchemaInputs = [];
            this.$bvModal.hide('schema-input-order-change-modal');
        },
        async saveOrder() {
            try {
                this.loading = true;

                await updateInputKeys({
                    items: this.localSchemaInputs.map((item,idx) => {
                        return {
                            ...item,
                            sort_order: idx
                        };
                    })
                });
                this.closeModal();
                this.$emit('schemaInputsOrderChanged');
                this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 순서 변경'), this.$t('스키마 입력 값 순서가 성공적으로 변경되었습니다.'));
            } catch (e) {
                console.error('Error saving order:', e);
                this.$showModal({
                    type: 'alert',
                    state: 'error',
                    title: this.$t('스키마 입력 값 순서 변경'),
                    message: this.$t('스키마 입력 값 순서 변경 중 오류가 발생했습니다. 개발팀에게 문의해주세요.'),
                    modalOptions: {
                        noCloseOnEsc: true,
                        noCloseOnBackdrop: true,
                    }
                });
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>
<style scoped lang="scss">
.default-modal {
    .modal-body {
        padding: 1.5rem 1.5rem;
    }

    .action-btns {
        display: flex;
        justify-content: flex-end;
        gap: 8px;

        .btn {
            width: 80px;

            &.md {
                width: 120px;
            }
        }
    }

    .tabs-container {
        .tab-content {
            min-height: 444px;
        }
    }

    .default-tabs {
        margin-top: -34px;
    }

    .order-change-container {
        padding: 1rem 0;
    }

    .order-list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #e9ecef;

        span {
            font-weight: 600;
        }

        small {
            font-size: 0.875rem;
        }
    }

    .draggable-list {
        .ghost-placeholder {
            opacity: 0.5;
            background: #e6f7ff;
            border: 2px dashed #007bff;
            border-radius: 8px;

            & > * {
                visibility: hidden;
            }
        }
    }

    .draggable-item {
        display: flex;
        align-items: center;
        padding: 0.75rem;
        margin-bottom: 0.5rem;
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: all 0.2s ease;

        &.dragging-item {
            opacity: 0.8;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            background: #fff;
        }

        .drag-handle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            margin-right: 0.75rem;
            color: #6c757d;
            cursor: grab;

            &:active {
                cursor: grabbing;
            }
        }

        .item-content {
            display: flex;
            align-items: center;
            flex: 1;
            gap: 0.75rem;

            .item-order {
                width: 32px;
                height: 32px;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 50%;
                font-weight: 600;
                font-size: 0.875rem;
            }

            .item-info {
                display: flex;
                align-items: center;

                .item-basic {
                    width: 100%;

                    .item-name {
                        font-weight: 500;
                    }

                    .item-code {
                        font-size: 0.875rem;
                    }
                }

                .vertical-divider {
                    width: 1px;
                    height: 45px;
                    background: #dee2e6;
                    margin: 0 1rem;
                }

                .item-desc-wrapper {
                    width: 515px;
                    height: 45px;
                    display: flex;
                    align-items: center;
                }

                .item-desc {
                    font-size: 0.8rem;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 1.5;
                }

            }
        }
    }

    .draggable-list:not(.is-dragging) .draggable-item:hover {
        border-color: #007bff;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
    }
}
</style>
